import { t as tt } from 'i18next';
import log from 'loglevel';
import remote from 'loglevel-plugin-remote';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AppFooter from './AppFooter';
import AppHeader from './AppHeader';
import AppMain from './AppMain';
import { ReactComponent as At } from './assets/svg/At.svg';
import { ReactComponent as CatalogLogo } from './assets/svg/Catalog.svg';
import { ReactComponent as ShowcaseLogo } from './assets/svg/CatalogMin.svg';
import { ReactComponent as ConsumerLogo } from './assets/svg/Consumer.svg';
import { ReactComponent as NotificationLogo } from './assets/svg/Notify.svg';
import { ReactComponent as ReqLogo } from './assets/svg/PermRequest.svg';
// import { ReactComponent as ToolsLogo } from './assets/svg/Tools.svg';
import { ReactComponent as StorageLogo } from './assets/img/home/header-storage.svg';
import { ROLE } from './components/common';
import { EMPTY_ROLE, PUBLIC_ROUTES, WELCOME_ROUTES } from './const';
import './css/common.css';
// import './css/style.min.css';
// import './css/welcome.min.css';
import { useAcc, useAuth, useBodyClass, useRole } from './hooks';
import './langs/i18n';
import { setRole, useLazyGetSummaryQuery } from './services/api';
import { BASE_URL } from './services/reauth';
import { useLazyGetCurrentAccountQuery, useLazyGetProfileQuery, useLazyGetRegistrationsQuery } from './services/users_v1';
import { CustomMenuItem } from './types/types';
import worker_script from './worker';

remote.apply(log, {
    url: BASE_URL + 'logger/',
    method: 'POST',
    timeout: 0,
    interval: 60000,
    level: 'warn',
    backoff: {
        multiplier: 10,
        jitter: 0.1,
        limit: 30000,
    },
    capacity: 500,
    stacktrace: {
        levels: ['trace', 'warn', 'error'],
        depth: 5,
        excess: 0,
    },
    format: remote.json
});

const worker = new Worker(worker_script);
worker.postMessage(document.baseURI);
worker.onmessage = function (e) {
    if (e.data === 'CHANGE') {
        const ret = confirm(tt('The page version is out of date and needs to be reloaded'));
        if (ret) {
            window.location.href = `/?t=${new Date().getTime()}`;
            // window.location.reload();
        }
    }
}

function App() {
    const [summaryPollingInterval, setSummaryPollingInterval] = useState(0);

    const [fetchProfile] = useLazyGetProfileQuery();
    const [fetchUserAccount] = useLazyGetCurrentAccountQuery();
    const [fetchSummary, { data: summary }] = useLazyGetSummaryQuery({ pollingInterval: summaryPollingInterval });
    const [fetchApproveRequests, { data: newUsers }] = useLazyGetRegistrationsQuery();

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const auth = useAuth();
    const acc = useAcc();
    const role = useRole();
    const { t } = useTranslation();

    const [menuItems, setMenuItems] = useState<CustomMenuItem[]>([]);
    const [bodyClasses, setBodyClasses] = useState<string[]>([]);

    useEffect(() => {
        const tmp = document.getElementById('text');
        sessionStorage.text = tmp?.innerHTML || '';
        tmp?.remove();
        const tmp2 = document.getElementById('text2');
        sessionStorage.text2 = tmp2?.innerHTML || '';
        tmp2?.remove();
    }, []);

    useEffect(() => {
        if (auth.user) {
            if (!auth.account) {
                fetchProfile(new Date().toISOString());
            }
        } else {
            setSummaryPollingInterval(0);
        }
    }, [auth.user, setSummaryPollingInterval]);

    useEffect(() => {
        if (auth.account) {
            // начинаем опросы только для авторизованного пользователя
            fetchProfile(new Date().toISOString());
            setSummaryPollingInterval(15000);
            fetchSummary();
            fetchUserAccount();
        }
    }, [auth.account, auth.user, setSummaryPollingInterval, fetchSummary, fetchUserAccount, fetchProfile]);

    useEffect(() => {
        if (auth.account && role.label === ROLE.SITE_ADMIN) {
            fetchApproveRequests();
        }
    }, [auth.account, fetchApproveRequests, role.label]);

    // глобальная раскраска зависящая от типа аккаунта (белый - поставщик, черный - потребитель, желтый - админ, голубой - модератор/партнер)
    useEffect(() => {
        const tmpClasses: string[] = [];
        if (WELCOME_ROUTES.some((route) => location.pathname.startsWith(route)) || (!auth.user && !PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route)))) {
            tmpClasses.push('page-welcome');
            tmpClasses.push('page-welcome-about');
        } else if (auth.account) {
            const consumerRoles = [ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_AUDITOR, ROLE.CONSUMER_CONTENT_MANAGER, ROLE.CONSUMER_ENGINEER, ROLE.CONSUMER_OWNER];
            if (!consumerRoles.includes(role.label) && (!(location.pathname.startsWith('/content/view/') || location.pathname.startsWith('/content/service/view/'))) && (!location.search.includes('view=true'))) {
                tmpClasses.push('page-producer');
                if (role.label === ROLE.SITE_ADMIN) {
                    tmpClasses.push('access-admin');
                } else if ([
                    ROLE.PARTNER_MODERATOR_ADMIN, ROLE.PARTNER_MODERATOR_MODERATOR, ROLE.PARTNER_MODERATOR_OWNER,
                    ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_CONTENT_MANAGER, ROLE.SITE_MODERATOR,
                    ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
                ].includes(role.label)) {
                    tmpClasses.push('access-mod');
                } else if (role.label === ROLE.GUEST_GUEST) {
                    tmpClasses.push('access-guest');
                }
            } else {
                tmpClasses.push('page-subscriber');
            }
        } else if (auth.user) {
            tmpClasses.push('page-subscriber');
        } else {
            dispatch(setRole(EMPTY_ROLE));
            // if (location.pathname === '/') {
            tmpClasses.push('page-home');
            // }
        }
        if (location.pathname.startsWith('/sdn')) {
            tmpClasses.push('page-storage');
        }
        if (location.pathname.startsWith('/sdn-public')) {
            tmpClasses.push('page-producer');
            tmpClasses.push('page-storage-guest');
        }
        setBodyClasses(tmpClasses);
    }, [location.pathname, location.search, auth.account, auth.user, dispatch, role, role.label]);

    useBodyClass(bodyClasses);

    const handleNavigation = useCallback(() => {
        if (window.scrollY === 0) {
            document.documentElement.classList.remove('_header-fixed');
        } else {
            document.documentElement.classList.add('_header-fixed');
        }
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleNavigation);

        return () => {
            window.removeEventListener('scroll', handleNavigation);
        };
    }, [handleNavigation]);

    const items = useMemo(() => [
        {
            label: t('Showcase'),
            icon: <ShowcaseLogo />,
            command: () => navigate('/showcase'),
            link: '/showcase',
            avail: [
                ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_AUDITOR, ROLE.CONSUMER_CONTENT_MANAGER, ROLE.CONSUMER_ENGINEER, ROLE.CONSUMER_OWNER,
                // ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_CONTENT_MANAGER,
                ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
                ROLE.GUEST_GUEST,
            ]
        },
        {
            label: t('Registrations'),
            icon: <ReqLogo />,
            command: () => navigate('/requests'),
            data: newUsers?.results.length,
            link: '/requests',
            avail: [ROLE.SITE_ADMIN]
        },
        // {
        //     label: t('Requests'),
        //     icon: <ReqLogo />,
        //     data: Number(summary?.results.access_requests.opened) + Number(summary?.results.access_requests.pending),
        //     command: () => navigate('/access'),
        //     link: '/access',
        //     noend: true,
        //     avail: [
        //         ROLE.CONSUMER_OWNER, ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_CONTENT_MANAGER,
        //         ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_CONTENT_MANAGER,
        //         ROLE.SITE_ADMIN,
        //         ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN,
        //         ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN,
        //     ]
        // },
        {
            label: t('Deals'),
            icon: <ReqLogo />,
            data: Number(summary?.results.access_requests.opened) + Number(summary?.results.access_requests.pending),
            command: () => navigate('/deals'),
            link: '/deals',
            noend: true,
            avail: [
                ROLE.CONSUMER_OWNER, ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_CONTENT_MANAGER,
                ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_CONTENT_MANAGER,
                ROLE.SITE_ADMIN,
                ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_CONTENT_MANAGER,
                ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER
            ]
        },
        {
            label: t('Notifications'),
            icon: <NotificationLogo />,
            command: () => navigate('/notifications'),
            data: summary?.results.notifictions.new,
            link: '/notifications',
            avail: [
                ROLE.CONSUMER_OWNER, ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_CONTENT_MANAGER, ROLE.CONSUMER_AUDITOR,
                ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_CONTENT_MANAGER, ROLE.PROVIDER_AUDITOR,
                ROLE.SITE_ADMIN, ROLE.SITE_MODERATOR,
                ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_CONTENT_MANAGER,
                ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
            ]
        },
        {
            label: t('Users'),
            icon: <ConsumerLogo />,
            command: () => navigate('/users'),
            link: '/users',
            avail: [ROLE.SITE_ADMIN]
        },
        {
            label: t('Accounts'),
            icon: <At />,
            command: () => navigate('/accounts'),
            link: '/accounts',
            avail: [ROLE.SITE_ADMIN]
        },
        {
            label: t('Participants'),
            icon: <ConsumerLogo />,
            command: () => navigate('/consumers'),
            link: '/consumers',
            avail: [ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_CONTENT_MANAGER, ROLE.PARTICIPANT]
        },
        {
            label: t('Participants'),
            icon: <ConsumerLogo />,
            command: () => navigate('/partners'),
            link: '/partners',
            avail: [ROLE.CONSUMER_OWNER, ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_CONTENT_MANAGER, ROLE.PARTICIPANT]
        },
        {
            label: t('storage:STORAGE'),
            icon: <StorageLogo className="header__nav-link-icon" />,
            command: () => navigate('/sdn'),
            link: '/sdn',
            noend: true,
            avail: [
                ROLE.SITE_ADMIN,
                ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_CONTENT_MANAGER, ROLE.PROVIDER_STORAGE_OPERATOR,
                ROLE.CONSUMER_OWNER, ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_CONTENT_MANAGER, ROLE.CONSUMER_STORAGE_OPERATOR,
                ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_CONTENT_MANAGER,
                ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_OWNER,ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
                ROLE.PARTNER_MODERATOR_OWNER, ROLE.PARTNER_MODERATOR_ADMIN,
                ROLE.PARTNER_OWNER, ROLE.PARTNER_ADMIN, ROLE.PARTNER_STORAGE_OPERATOR,
            ]
        },
        {
            label: t('Catalog'),
            icon: <CatalogLogo />,
            command: () => navigate('/content'),
            link: '/content',
            avail: [
                ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_ENGINEER, ROLE.PROVIDER_AUDITOR, ROLE.PROVIDER_CONTENT_MANAGER,
                ROLE.SITE_ADMIN, ROLE.SITE_MODERATOR,
            ]
        },
    ], [navigate, newUsers?.results.length, summary, t]);

    // список элементов верхнего меню
    useEffect(() => {
        if (auth.account) {
            if (role.label !== ROLE.UNKNOWN) {
                setMenuItems(items.filter((item) => {
                    if (item.avail.includes(ROLE.PARTICIPANT)) {
                        if (acc.hideParticipants) {
                            return false;
                        }
                    }
                    return !item.avail || item.avail?.includes(role.label);
                }));
            } else {
                setMenuItems([]);
            }
        } else {
            setMenuItems([]);
        }
    }, [role, role.label, items, auth.account, auth.user, acc]);

    const LazyStyleCssComponent = React.lazy(() => import('./components/CssComponent'));
    const LazyWelcomeCssComponent = React.lazy(() => import('./components/WelcomeCssComponent'));

    return <>
        <React.Suspense fallback={<></>}>
            {(WELCOME_ROUTES.some((route) => location.pathname.startsWith(route)) || (!auth.user && !PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route)))) ? <LazyWelcomeCssComponent /> : <LazyStyleCssComponent />}
        </React.Suspense>
        <AppHeader items={menuItems} />
        <AppMain />
        {(location.pathname === '/' || WELCOME_ROUTES.some((route) => location.pathname.startsWith(route))) && <AppFooter />}
    </>
}

export default App;
